/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import {makeStyles} from '@material-ui/core/styles';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {getFileHistory, loadDeviceFiles} from '@ion/core/src/services/Connect';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {
  sortByString
} from '@ion/core/src/utils/ArrayUtil';
import {
  getDeviceIdentifierFromType,
  getScriptFileDescription
} from '@ion/core/src/utils/DeviceUtils';
import {
  clone,
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  isEmpty
} from '@ion/core/src/utils/StringUtil';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {AgGridType} from '@ion/dls/src/grid/IonGrid';
import {ColumnChooserIcon} from '@ion/dls/src/icons/ColumnChooserIcon';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import useDeviceTypes from '@ion/query/src/hooks/useDeviceTypes';
import DeviceFileTypeChooser from './DeviceFileTypeChooser';
import FileHistoryGrid from './FileHistoryGrid';
import {
  filterByAllFileTypes,
  filterByplatformId,
  getFileHistoryByStatus,
  getPlatFormIdLists
} from './ViewDeviceEditorUtils';

export interface FilesTabType {
  selectedTab: Number;
  tabIndex: Number;
  device?: Device;
  currentFileHistory?: GenericObject[];
  isCurrentHistoryLoading?: boolean;
  pendingFileHistory?: GenericObject[];
  pendingHistoryLoading?: boolean;
  selectedJobId?: (jobId: number) => void;
  isToggleSwitchGrid?: boolean;
  isShowColumnChooserIcon?: boolean;
  isShowRefreshButton?: boolean;
  setScriptDesc?: Function;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  flexStyle : {
    display   : 'flex',
    marginTop : '20px'
  },
  typeStyle : {
    width       : '230px',
    marginRight : '10px'
  },
  dropdownStyle : {
    width       : '180px',
    marginRight : '10px'
  },
  retrievedStyle : {
    marginTop : '28px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    padding       : '5px 5px 5px 10px',
    textTransform : 'capitalize'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color : dlsColor('aaBlue')
  },
  alignStyle : {
    display                        : 'flex',
    justifyContent                 : 'space-between',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column'
    }
  },
  refresh : {
    marginTop : '5px',
    display   : 'inline-flex'
  },
  actions : {
    display                        : 'flex',
    marginTop                      : '50px',
    [theme.breakpoints.down('sm')] : {
      marginTop : '5px'
    }
  },
  lastRetrievedDate : {
    marginTop : '15px'
  },
  buttonStyle : {
    marginTop                      : '50px',
    [theme.breakpoints.down('sm')] : {
      marginTop : '10px'
    }
  },
  gridContainer : {
    margin : '20px 10px 20px 0',
    height : 'calc(100vh - 434px)'
  },
  detailsTab : {
    height : '100%'
  }
}));

/**
 * FilesTab component that displays the Device manufacturing
 * data for the selected Device Details tab.
 */
export const FilesTab = ({
  device,
  selectedJobId,
  currentFileHistory,
  pendingFileHistory,
  tabIndex = 0,
  selectedTab = 0,
  isToggleSwitchGrid = false,
  isShowColumnChooserIcon = false,
  isShowRefreshButton = true,
  setScriptDesc,
  pendingHistoryLoading = false,
  isCurrentHistoryLoading = false
}: FilesTabType) => {
  const classes     = useStyles();
  const gridRef     = useRef<GenericObject>();
  const currentFile = translate('admin-devices:view.files.current')?.toUpperCase();
  const logFile     = translate('admin-devices:view.files.log')?.toUpperCase();

  const [isLoading, setIsLoading]                   = useState(false);
  const [fileHistory, setFileHistory]               = useState([]);
  const [rowData, setRowData]                       = useState([]);
  const [deviceType, setDeviceType]                 = useState(null);
  const [fileType, setFileType]                     = useState(null);
  const [fileChooserList, setFileChooserList]       = useState([]);
  const [fileTab, setFileTab]                       = useState(currentFile);
  const [platformIdList, setPlatformIdList]         = useState([]);
  const [selectedPlatformId, setSelectedPlatformId] = useState('');
  const [refresh, setRefresh]                       = useState(false);
  const [isFailures, setIsFailures]                 = useState<boolean>(false);
  const [statusOptions, setStatusOptions]           = useState([]);
  const [fileStatus, setFileStatus]                 = useState<string>('');
  const [api, setApi]                               = useState<AgGridType>(null);
  const {deviceTypes}                               = useDeviceTypes();

  const {options} = useMemo(() => (
    translate('admin-devices:view.files', {returnObjects: true} || {})
  ), []);

  useEffect(() => {
    if (!isObjectEmpty(options)) {
      const statusList = [
        ...options?.failedStatus,
        ...options?.cancelledStatus,
        ...options?.completedStatus,
        ...options?.wipStatus
      ];
      setStatusOptions(statusList);
    }
  }, [options]);

  const getFilteredData = useCallback(function(data) {
    let filteredFileHistory = clone(data);
    if (data?.length) {
      if (deviceType && fileType) {
        filteredFileHistory = filterByplatformId(filteredFileHistory, selectedPlatformId);
      } else if (deviceType) {
        filteredFileHistory = filterByAllFileTypes(filteredFileHistory, platformIdList);
      }
      if (fileTab === logFile && fileStatus) {
        filteredFileHistory = getFileHistoryByStatus(filteredFileHistory, fileStatus);
      }
    }
    setRowData(filteredFileHistory);
  }, [
    deviceType,
    fileType,
    fileTab,
    fileStatus
  ]);

  const onFileTypeChange = useCallback(function(value) {
    setFileType(value);
    setSelectedPlatformId(value?.platformId);
  }, []);

  const handleDeviceFileType = useCallback(function({
    deviceTypeOption,
    fileTypeOption
  }) {
    if (isEmpty(deviceTypeOption) || deviceTypeOption) {
      setDeviceType(deviceTypeOption);
      const fileTypes = sortByString(deviceTypeOption?.fileTypes, 'name', false);
      setFileChooserList(fileTypes);
      setPlatformIdList(getPlatFormIdLists(fileTypes));
    } else {
      setFileChooserList([]);
      setPlatformIdList([]);
    }
    if (fileTypeOption) {
      onFileTypeChange({...fileTypeOption});
    } else {
      setFileType(null);
      setSelectedPlatformId('');
    }
  }, []);

  useEffect(() => {
    if (api) {
      api?.setGridOption('loading', (isLoading || refresh));
    }
  }, [isLoading, refresh, api]);

  const toggleCurrent = useCallback(function(e) {
    setFileTab(e.target.innerText?.toUpperCase());
    setRowData([]);
    refreshData();
  }, []);

  const refreshData = useCallback(function() {
    setRefresh(true);
    setTimeout(() => {
      setRefresh(false);
    }, 500);
  }, []);

  useEffect(() => {
    refreshData();
  }, [
    platformIdList,
    selectedPlatformId,
    currentFileHistory,
    isCurrentHistoryLoading,
    device,
    fileStatus
  ]);

  useEffect(() => {
    setIsLoading(isCurrentHistoryLoading || pendingHistoryLoading);
  }, [pendingHistoryLoading, isCurrentHistoryLoading]);

  const ruleEngineFailureMsg= [
    'UNKNOWN_DEVICE',
    'UNKNOWN_FILE',
    'FILE_CAN_NOT_BE_ASSIGNED',
    'FILE_CAN_NOT_BE_ASSIGNED_DELTA_VERSION_RULE',
    'FILE_CAN_NOT_BE_ASSIGNED_DELTA_VERSION_RULE_DEVICE_HAS_NO_VERSION',
    'FILE_RULE_TWO_FAILED',
    'FILE_RULE_VBUS_SUPPORT_FAILED',
    'DEVICE_PLATFORM_IS_SUPPORTED_BY_FILE_FAILED',
    'EXCEPTION',
    'FILE_RULE_LMU_APP_ID_MATCH_FAILED',
    'FILE_RULE_LMU_APP_ID_ASSOCIATION_FAILED',
    'DEVICE_APP_ID_NOT_SUPPORTED',
    'ID_REPORT_NOT_FOUND'
  ];

  useEffect(() => {
    let failures;
    if (pendingFileHistory?.length > 0) {
      failures = pendingFileHistory.filter((files) => {
        if ((files.transactionStatus.name === 'FAILED') &&
            (ruleEngineFailureMsg.includes(files?.otaDiagMessage))
        ) {
          return (files.lastUpdateTime > moment().subtract(24, 'hours').toISOString());
        }
      });
    }
    setIsFailures(failures?.length > 0);
  }, [pendingFileHistory]);

  useEffect(() => {
    const deviceIdentifier = getDeviceIdentifierFromType(device);
    // load file history ...
    const getFileHistoryData = async function() {
      setIsLoading(true);
      setFileHistory([]);
      setRowData([]);
      try {
        let data: any;
        if (fileTab === currentFile) {
          data = await addFileNamesToFileHistory(currentFileHistory, setScriptDesc);
        } else {
          data = await getFileHistory(deviceIdentifier);
        }
        setIsLoading(false);
        setFileHistory(data);
        getFilteredData(data);
      } catch (err) {
        setIsLoading(false);
        setFileHistory([]);
        setRowData([]);
      }
    };

    if (refresh && deviceIdentifier && !isCurrentHistoryLoading) {
      getFileHistoryData();
    }
  }, [refresh, device, currentFileHistory, isCurrentHistoryLoading, setScriptDesc]);

  const onShowColumnChooser = useCallback((val) =>
    function() {
      gridRef.current?.toggleColumnChooser?.(val);
    }
  , []);

  return (
    <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab} hidden={selectedTab !== tabIndex}>
      <div className={classes.alignStyle}>
        <div className={classes.flexStyle}>
          <div className={classes.typeStyle}>
            <DeviceFileTypeChooser deviceTypes={deviceTypes}
              selectedType={''}
              onChange={handleDeviceFileType} />
          </div>
          {fileTab === logFile && (
            <>
              <div className={classes.dropdownStyle}>
                <Dropdown displaySelectedLabel
                  includeNone
                  noneOptionLabel={translate('admin-devices:view.files.all')}
                  noneOptionValue={''}
                  options={statusOptions}
                  fieldLabel={translate('admin-devices:view.files.statusLabel')}
                  value={fileStatus}
                  name={'status'}
                  onChange={function(event, {value}) {
                    setFileStatus(value);
                  }} />
              </div>
            </>
          )}
        </div>
        <div className={classes.actions}>
          {isShowColumnChooserIcon && (
            <IconButton icon={<ColumnChooserIcon />}
              onClick={onShowColumnChooser(true)} />
          )}
        </div>
        <div className={classes.buttonStyle}>
          {isShowRefreshButton && (
            <div className={classes.refresh}>
              <RefreshGridButton onRefresh={refreshData} />
            </div>
          )}
          <ButtonGroup disableElevation variant='outlined' color='primary'>
            <Button defaultChecked
              variant={fileTab === currentFile ? 'contained' : 'outlined'}
              onClick={toggleCurrent}>
              {translate('admin-devices:view.files.current')}
            </Button>
            <ConditionalRender condition={isFailures} >
              <Badge color='error' variant='dot' badgeContent=' '>
                <Button variant={fileTab === logFile ? 'contained' : 'outlined'}
                  onClick={toggleCurrent}
                  color="primary">
                  {translate('admin-devices:view.files.log')}
                </Button>
              </Badge>
            </ConditionalRender>
            <ConditionalRender condition={!isFailures} >
              <Button variant={fileTab === logFile ? 'contained' : 'outlined'}
                onClick={toggleCurrent}
                color="primary">
                {translate('admin-devices:view.files.log')}
              </Button>
            </ConditionalRender>
          </ButtonGroup>
        </div>
      </div>
      <div className={classes.gridContainer}>
        <FileHistoryGrid rowData={rowData}
          selectedJobId={selectedJobId}
          isLogsView={fileTab === logFile}
          ref={gridRef}
          setApi={setApi}
          loading={isLoading}
          isToggleSwitchGrid={isToggleSwitchGrid} />
      </div>
    </div>
  );
};

/**
 * Function to augment the file history with file names, if they exist. First, a list of
 * unique externalFileIds is created from all file history entries that contain an
 * externalFileId in their newDeviceFile data. Next, device file information (containing
 * file names) for those IDs is fetched. Finally, the file history entries are updated
 * with the file names.
 */
const addFileNamesToFileHistory = async(data, setScriptDesc) => {
  if (data) {
    const fileIds = data?.historyByPlatform?.map(item => {
      let getExternalFileId;
      const currentObj = item?.current;
      currentObj?.forEach(currentObjVal => {
        getExternalFileId = (currentObjVal?.externalFileId);
      });
      return getExternalFileId;
    });

    const cleanFileIds = fileIds?.filter(e => e === 0 ? true : e);

    if (cleanFileIds?.length) {
      const result = await loadDeviceFiles({
        pageId               : 1,
        pageSize             : 100,
        includeAccountFilter : false,
        filterOn             : {id: `${cleanFileIds.join(',')}`}
      });
      const description = getScriptFileDescription(result?.items);
      setScriptDesc(description);
      if (result?.items) {
        const updated = [];
        data?.historyByPlatform?.forEach(dataItem => {
          const updatedItem = {...dataItem};
          const {current} = dataItem;
          current?.forEach(currentObjVal => {
            if (currentObjVal?.externalFileId) {
              result?.items?.forEach(({id, name}) => {
                if (Number(currentObjVal?.externalFileId) === Number(id)) {
                  updatedItem.newFileName = name || '';
                }
              });
            }
          });
          updated.push(updatedItem);
        });
        return updated;
      }
    }
  }
  return data?.historyByPlatform || [];
};

export default FilesTab;
